import { useState } from 'react';
import { X, Activity, History } from 'lucide-react';
import dayjs from 'lib/dayjs';
import { useGetUserIpLogsQuery } from '../../../redux/features/settings/settingsApi';
import Pagination from '../../../components/ui/Pagination';

const IpLogModal = ({ isOpen, onClose, userIpId, user, isSelf = false }) => {
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const {
    data: logsData,
    isFetching: isLoading,
    isError,
  } = useGetUserIpLogsQuery({ userIpId, pageNo, pageSize }, { skip: !isOpen || !userIpId });

  if (!isOpen) return null;

  const logs = logsData?.logs || [];
  const totalCount = logsData?.total || 0;
  const totalPages = logsData?.totalPages || 0;

  const getUserAgent = (agent = '') => {
    let os = 'Unknown';
    let browser = 'Unknown';

    if (/Windows/i.test(agent)) os = 'Windows';
    else if (/Mac OS|Macintosh/i.test(agent)) os = 'Mac';
    else if (/Linux/i.test(agent)) os = 'Linux';
    else if (/Android/i.test(agent)) os = 'Android';
    else if (/iPhone|iPad/i.test(agent)) os = 'iOS';

    if (/Chrome/i.test(agent) && !/Edg|OPR/i.test(agent)) browser = 'Chrome';
    else if (/Edg/i.test(agent)) browser = 'Edge';
    else if (/Firefox/i.test(agent)) browser = 'Firefox';
    else if (/Safari/i.test(agent) && !/Chrome/i.test(agent)) browser = 'Safari';
    else if (/OPR/i.test(agent)) browser = 'Opera';

    return `${os} / ${browser}`;
  };

  const getActionColor = (action) => {
    switch (action?.toLowerCase()) {
      case 'create':
        return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      case 'update':
        return 'bg-blue-100 text-blue-700 border border-blue-200';
      case 'delete':
        return 'bg-red-100 text-red-700 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  // Component for showing diff (old vs new)
  const DiffCell = ({ oldVal, newVal, fallback }) => {
    const hasChanged = oldVal && newVal && oldVal !== newVal;

    if (!hasChanged) {
      return (
        <div className="flex">
          <span
            className="text-gray-900 font-medium text-xs truncate block"
            title={newVal || oldVal || fallback}
          >
            {newVal || oldVal || fallback || '---'}
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col">
        {/* New Value - Top Right */}
        <div className="flex">
          <span
            className="text-gray-600 font-medium line-through text-[12px] leading-tight  decoration-gray-600"
            title={`Old: ${oldVal}`}
          >
            {oldVal}
          </span>
        </div>
        <div className="flex">
          <span
            className="text-gray-900 font-bold text-[12px] leading-tight"
            title={`New: ${newVal}`}
          >
            {newVal}
          </span>
        </div>

        {/* Old Value - Bottom Left */}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 rounded-xl">
                <History className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">User IP Logs</h3>
                {!isSelf && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    {user?.name || 'N/A'} • Log History
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-6 bg-gray-50/30">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[140px]">
                        User
                      </th>
                      <th className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[160px]">
                        Title
                      </th>
                      <th className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[160px]">
                        IP Address
                      </th>
                      <th className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[140px]">
                        OS / Browser
                      </th>
                      <th className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[140px]">
                        Updated At
                      </th>
                      <th className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[100px]">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {isLoading ? (
                      <tr>
                        <td colSpan={6} className="py-16">
                          <div className="flex flex-col items-center justify-center space-y-3">
                            <div className="h-8 w-8 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin" />
                            <p className="text-gray-500 text-sm font-medium">
                              Loading activity logs...
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : isError ? (
                      <tr>
                        <td colSpan={6} className="py-16 px-6">
                          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-center max-w-md mx-auto">
                            <p className="font-semibold mb-1">Failed to load logs</p>
                            <p className="text-sm text-red-600/80">Please try again later</p>
                          </div>
                        </td>
                      </tr>
                    ) : logs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-16">
                          <div className="text-center">
                            <div className="mx-auto w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                              <Activity className="h-7 w-7 text-gray-400" />
                            </div>
                            <p className="text-gray-900 font-medium">No logs found</p>
                            <p className="text-gray-500 text-sm mt-1">
                              No activity recorded for this IP
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      logs.map((log, index) => (
                        <tr
                          key={log.id || index}
                          className={`hover:bg-blue-50/40  align-center transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                        >
                          {/* User */}
                          <td className="px-4 py-3.5 align-middle  whitespace-nowrap">
                            <div className="flex items-center">
                              <span
                                className="text-gray-900 font-medium text-sm truncate max-w-[120px]"
                                title={log?.name}
                              >
                                {log?.name || 'Unknown'}
                              </span>
                            </div>
                          </td>

                          {/* Title with Diff */}
                          <td className="px-4 py-3.5 align-middle whitespace-nowrap">
                            <DiffCell
                              oldVal={log.old_name}
                              newVal={log.new_name}
                              fallback={log.name}
                            />
                          </td>

                          {/* IP with Diff */}
                          <td className="px-4  align-middle py-3.5 whitespace-nowrap font-mono">
                            <DiffCell oldVal={log.old_ip} newVal={log.new_ip} />
                          </td>

                          {/* OS/Browser */}
                          <td className="px-4 py-3.5 align-middle">
                            <div
                              className="text-gray-600 text-xs truncate max-w-[120px]"
                              title={log.user_agent}
                            >
                              {getUserAgent(log.user_agent)}
                            </div>
                          </td>

                          {/* Date */}
                          <td className="px-4 py-3.5 align-middle whitespace-nowrap">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-gray-900 text-xs font-medium">
                                {dayjs(log.created_at).format('MMM DD, YYYY')}
                              </span>
                              <span className="text-[11px] text-gray-400 font-mono">
                                {dayjs(log.created_at).format('hh:mm:ss A')}
                              </span>
                            </div>
                          </td>

                          {/* Action */}
                          <td className="px-4 py-3.5 align-middle whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${getActionColor(log.action_type)}`}
                            >
                              {log.action_type}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}

                    {/* Fill empty rows to maintain height consistency */}
                    {!isLoading &&
                      !isError &&
                      logs.length > 0 &&
                      logs.length < pageSize &&
                      [...Array(pageSize - logs.length)].map((_, i) => (
                        <tr
                          key={`empty-${i}`}
                          className={`h-[65px] ${(logs.length + i) % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                        >
                          <td colSpan={6}></td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="border-t border-gray-200 px-4 py-3 bg-gray-50/50">
                  <Pagination
                    currentPage={pageNo}
                    totalItems={totalCount}
                    pageSize={pageSize}
                    onPageChange={setPageNo}
                    onPageSizeChange={setPageSize}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IpLogModal;
